import { useState } from "react";
import "./App.css";
import AdvancedResults from "./components/AdvancedResults";
import EuaHedgingResults from "./components/EuaHedgingResults";
import FxHedgingResults from "./components/FxHedgingResults";
import Results from "./components/Results";

const API = process.env.REACT_APP_API_URL || "";

const normalizeNumber = (v) => {
    if (v === undefined || v === null || v === "") return null;
    return Number(String(v).replaceAll(",", ""));
};

// 포맷팅 함수들
const formatNumber = (value, type) => {
    const num = normalizeNumber(value);
    if (num === null || num === undefined || isNaN(num)) return "";

    switch (type) {
        case "ratio":
            return num.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 4
            });
        case "factor":
            return num.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        case "price":
            return num.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        case "amount":
            return num.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            });
        default:
            return num.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
    }
};

// 필드 타입 매핑
const getFieldType = (key) => {
    const typeMap = {
        specific_emission: "ratio",
        benchmark: "ratio",
        factor: "factor",
        price: "price",
        eua_price: "price",
        kau_price: "price",
        import_amount: "amount",
        electricity_consumption: "ratio",
        electricity_factor: "ratio"
    };
    return typeMap[key] || "ratio";
};

// Default 값 정의
const DEFAULT_SCOPE1 = {
    specific_emission: "2.00",
    benchmark: "1.50",
    factor: "97.5",
    price: "85.00",
    import_amount: "10,000"
};

const DEFAULT_SCOPE12 = {
    specific_emission: "1.50",
    electricity_consumption: "2.08",
    electricity_factor: "0.45",
    benchmark: "1.00",
    factor: "97.5",
    price: "85.00",
    import_amount: "10,000"
};

const DEFAULT_ADVANCED = {
    specific_emission: "1.7500",
    benchmark: "1.2500",
    factor: "97.5",
    eua_price: "97.00",
    kau_price: "5.00",
    import_amount: "3,000,000"
};

const DEFAULT_FX_HEDGING = {
    hedging_start_date: "2026-03-15",
    start_spot_price: "1,150.00",
    start_futures_price: "1,155.00",
    cbam_payment: "12,287,570",
    hedging_end_date: "2027-05-15",
    end_spot_price: "1,200.00",
    end_futures_price: "1,200.00",
    adj_hedge_ratio: "1.2000"
};

const DEFAULT_EUA_HEDGING = {
    hedging_start_date: "2026-03-15",
    start_spot_price: "90.00",
    start_futures_price: "92.00",
    num_certifications: "14,568,055",
    hedging_end_date: "2027-05-15",
    end_spot_price: "110.00",
    end_futures_price: "110.00",
    adj_hedge_ratio: "1.1500"
};

export default function App() {
    const [version, setVersion] = useState("basic");
    const [tab, setTab] = useState("scope1");

    // -------- Scope 1 --------
    const [s1, setS1] = useState(DEFAULT_SCOPE1);
    const [s1Result, setS1Result] = useState({});

    // -------- Scope 1 + 2 --------
    const [s12, setS12] = useState(DEFAULT_SCOPE12);
    const [s12Result, setS12Result] = useState({});

    // -------- Advanced --------
    const [adv, setAdv] = useState(DEFAULT_ADVANCED);
    const [advResult, setAdvResult] = useState({});

    // -------- FX Hedging --------
    const [fxHedging, setFxHedging] = useState(DEFAULT_FX_HEDGING);
    const [fxHedgingResult, setFxHedgingResult] = useState({});

    // -------- EUA Hedging --------
    const [euaHedging, setEuaHedging] = useState(DEFAULT_EUA_HEDGING);
    const [euaHedgingResult, setEuaHedgingResult] = useState({});

    // -------- 에러 상태 추가 --------
    const [errors, setErrors] = useState({});

    const validateInputs = (data, requiredFields) => {
        const newErrors = {};
        requiredFields.forEach(field => {
            const value = normalizeNumber(data[field]);
            if (value === null || value === undefined || isNaN(value)) {
                newErrors[field] = "필수 입력 항목입니다";
            } else if (value < 0) {
                newErrors[field] = "양수를 입력해주세요";
            }
        });
        return newErrors;
    };

    const post = async (url, data, setter, requiredFields) => {
        // 클라이언트 검증
        const validationErrors = validateInputs(data, requiredFields);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            alert("입력값이 올바르지 않습니다.\n필수 항목을 확인해주세요.");
            return;
        }

        try {
            // 서버 전송용 데이터 정규화
            const normalizedData = {};
            Object.keys(data).forEach(key => {
                normalizedData[key] = normalizeNumber(data[key]);
            });

            const res = await fetch(`${API}${url}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(normalizedData)
            });

            if (!res.ok) {
                const err = await res.json();

                // 서버 에러 처리
                if (err.details) {
                    const serverErrors = {};
                    err.details.forEach(d => {
                        const field = d.loc[d.loc.length - 1];
                        const fieldMap = {
                            electricity_factor: "electricity_factor",
                            specific_emission: "specific_emission",
                            benchmark: "benchmark",
                            import_amount: "import_amount",
                            price: "price",
                            eua_price: "eua_price",
                            kau_price: "kau_price",
                            factor: "factor",
                            electricity_consumption: "electricity_consumption"
                        };
                        if (fieldMap[field]) {
                            serverErrors[fieldMap[field]] = d.msg;
                        }
                    });
                    setErrors(serverErrors);
                }

                alert("입력값이 올바르지 않습니다.\n숫자 형식을 확인해주세요.");
                return;
            }

            // 성공 시 에러 초기화
            setErrors({});
            setter(await res.json());
        } catch (e) {
            alert("서버와 통신 중 오류가 발생했습니다.");
        }
    };

    return (
        <div className={`container ${(version === "fx-hedging" || version === "eua-hedging") ? "fx-hedging-mode" : ""}`}>
            {/* ---------- Header ---------- */}
            <div className="title-bar">
                <div>
                    <span className="brand">NAMU EnR</span> |
                    <span className="app-title"> EU-CBAM Cost Analytics Simulator</span>
                </div>
                <select
                    className="version-select"
                    value={version}
                    onChange={e => {
                        setVersion(e.target.value);
                        setS1Result({});
                        setS12Result({});
                        setAdvResult({});
                        setFxHedgingResult({});
                        setEuaHedgingResult({});
                        setErrors({});
                    }}
                >
                    <option value="basic">Basic v1.2</option>
                    <option value="advanced">Advanced v1.0</option>
                    <option value="fx-hedging">FX Hedging v1.2</option>
                    <option value="eua-hedging">EUA Hedging v1.2</option>
                </select>
            </div>

            {/* =================================================
                BASIC
                ================================================= */}
            {version === "basic" && (
                <>
                    <div className="tab-box">
                        <div
                            className={tab === "scope1" ? "tab active" : "tab"}
                            onClick={() => {
                                setTab("scope1");
                                setErrors({});
                            }}
                        >
                            Scope 1
                        </div>
                        <div
                            className={tab === "scope12" ? "tab active" : "tab"}
                            onClick={() => {
                                setTab("scope12");
                                setErrors({});
                            }}
                        >
                            Scope 1 + Scope 2
                        </div>
                    </div>

                    {tab === "scope1" && (
                        <>
                            <div className="scope-header">Scope 1</div>
                            <InputCard
                                fields={[
                                    ["Specific Embedded Emissions", "specific_emission"],
                                    ["EU-CBAM Benchmark Emissions", "benchmark"],
                                    // { type: "divider", style: "single" }, // 홑선 구분
                                    ["EU-CBAM Factor", "factor", "%"],
                                    ["EU-ETS EUA Auction Price", "price", "€"],
                                    ["Import Amount", "import_amount", "amount"]
                                ]}
                                state={s1}
                                setState={setS1}
                                errors={errors}
                            />

                            <div className="button-row">
                                <button onClick={() => {
                                    setS1({});
                                    setS1Result({});
                                    setErrors({});
                                }}>Clear</button>
                                <button onClick={() => {
                                    const requiredFields = ["specific_emission", "benchmark", "factor", "price", "import_amount"];
                                    const dataToSend = {
                                        ...s1,
                                        factor: normalizeNumber(s1.factor) / 100
                                    };
                                    post("/scope1", dataToSend, setS1Result, requiredFields);
                                }}>Calculate</button>
                            </div>

                            <Results result={s1Result} />
                        </>
                    )}

                    {tab === "scope12" && (
                        <>
                            <div className="scope-header">Scope 1 + Scope 2</div>
                            <InputCard
                                fields={[
                                    ["Specific Embedded Emissions", "specific_emission"],
                                    // { type: "divider", style: "single" },
                                    ["Electricity Consumption", "electricity_consumption", "ratio"],
                                    ["Electricity Emission Factor", "electricity_factor", "ratio"],
                                    { type: "divider", style: "single" },
                                    ["EU-CBAM Benchmark Emissions", "benchmark"],
                                    ["EU-CBAM Factor", "factor", "%"],
                                    ["EU-ETS EUA Auction Price", "price", "€"],
                                    ["Import Amount", "import_amount", "amount"]
                                ]}
                                state={s12}
                                setState={setS12}
                                errors={errors}
                            />

                            <div className="button-row">
                                <button onClick={() => {
                                    setS12({});
                                    setS12Result({});
                                    setErrors({});
                                }}>Clear</button>
                                <button onClick={() => {
                                    const requiredFields = ["specific_emission", "electricity_consumption", "electricity_factor", "benchmark", "factor", "price", "import_amount"];
                                    const dataToSend = {
                                        ...s12,
                                        factor: normalizeNumber(s12.factor) / 100
                                    };
                                    post("/scope12", dataToSend, setS12Result, requiredFields);
                                }}>Calculate</button>
                            </div>

                            <Results result={s12Result} />
                        </>
                    )}
                </>
            )}

            {/* =================================================
                ADVANCED
                ================================================= */}
            {version === "advanced" && (
                <>
                    <div className="scope-header">EU-CBAM CAS</div>
                    <InputCard
                        fields={[
                            ["Specific Embedded Emissions", "specific_emission", "ratio"],
                            ["EU-CBAM Benchmark Emissions", "benchmark", "ratio"],
                            // { type: "divider", style: "double" },
                            ["EU-CBAM Factor", "factor", "%"],
                            // { type: "divider", style: "single" },
                            ["EU-ETS EUA Auction Price", "eua_price", "€"],
                            ["Paid KAU Effective Price", "kau_price", "€"],
                            ["Import Amount", "import_amount", "amount"]
                        ]}
                        state={adv}
                        setState={setAdv}
                        errors={errors}
                    />

                    <div className="button-row">
                        <button onClick={() => {
                            setAdv({});
                            setAdvResult({});
                            setErrors({});
                        }}>Clear</button>
                        <button onClick={() => {
                            const requiredFields = ["specific_emission", "benchmark", "factor", "eua_price", "kau_price", "import_amount"];
                            const dataToSend = {
                                ...adv,
                                factor: normalizeNumber(adv.factor) / 100
                            };
                            post("/advanced", dataToSend, setAdvResult, requiredFields);
                        }}>Calculate</button>

                    </div>


                    <AdvancedResults result={advResult} />
                </>
            )}

            {/* =================================================
                FX HEDGING
                ================================================= */}
            {version === "fx-hedging" && (
                <>
                    <div className="fx-hedging-wrapper">
                        <div className="fx-hedging-headers">
                            <div className="fx-section-header">EU-CBAM Payment Hedging Strategy</div>
                            <div className="fx-section-header fx-green">FX EUR/KRW Futures Long Hedging Position</div>
                        </div>

                        <div className="card">
                            {/* Start Date - 병합 행 */}
                            <div className="row merged-row">
                                <span className="label">▶ Start Date</span>
                                <span className="value">
                                    <input
                                        type="date"
                                        value={fxHedging.hedging_start_date || ""}
                                        onChange={e => setFxHedging({ ...fxHedging, hedging_start_date: e.target.value })}
                                    />
                                </span>
                            </div>

                            {/* Spot Price & Future Price - 2열 */}
                            <div className="row split-row">
                                <div className="split-left">
                                    <span className="label">EUR/KRW Spot Price</span>
                                    <span className="value">
                                        <span className="unit-left">₩</span>
                                        <input
                                            type="text"
                                            value={fxHedging.start_spot_price || ""}
                                            onChange={e => setFxHedging({ ...fxHedging, start_spot_price: e.target.value })}
                                            onBlur={e => {
                                                const formatted = formatNumber(e.target.value, "price");
                                                if (formatted !== e.target.value) {
                                                    setFxHedging({ ...fxHedging, start_spot_price: formatted });
                                                }
                                            }}
                                            onFocus={e => {
                                                const num = normalizeNumber(e.target.value);
                                                if (num !== null && !isNaN(num)) {
                                                    setFxHedging({ ...fxHedging, start_spot_price: num.toString() });
                                                }
                                            }}
                                            placeholder="0"
                                        />
                                    </span>
                                </div>
                                <div className="split-right">
                                    <span className="label">EUR/KRW Future Price</span>
                                    <span className="value">
                                        <span className="unit-left">₩</span>
                                        <input
                                            type="text"
                                            value={fxHedging.start_futures_price || ""}
                                            onChange={e => setFxHedging({ ...fxHedging, start_futures_price: e.target.value })}
                                            onBlur={e => {
                                                const formatted = formatNumber(e.target.value, "price");
                                                if (formatted !== e.target.value) {
                                                    setFxHedging({ ...fxHedging, start_futures_price: formatted });
                                                }
                                            }}
                                            onFocus={e => {
                                                const num = normalizeNumber(e.target.value);
                                                if (num !== null && !isNaN(num)) {
                                                    setFxHedging({ ...fxHedging, start_futures_price: num.toString() });
                                                }
                                            }}
                                            placeholder="0"
                                        />
                                    </span>
                                </div>
                            </div>

                            {/* End Date - 병합 행 */}
                            <div className="row merged-row">
                                <span className="label">▶ End Date</span>
                                <span className="value">
                                    <input
                                        type="date"
                                        value={fxHedging.hedging_end_date || ""}
                                        onChange={e => setFxHedging({ ...fxHedging, hedging_end_date: e.target.value })}
                                    />
                                </span>
                            </div>

                            {/* Spot Price & Future Price - 2열 */}
                            <div className="row split-row">
                                <div className="split-left">
                                    <span className="label">EUR/KRW Spot Price</span>
                                    <span className="value">
                                        <span className="unit-left">₩</span>
                                        <input
                                            type="text"
                                            value={fxHedging.end_spot_price || ""}
                                            onChange={e => setFxHedging({ ...fxHedging, end_spot_price: e.target.value })}
                                            onBlur={e => {
                                                const formatted = formatNumber(e.target.value, "price");
                                                if (formatted !== e.target.value) {
                                                    setFxHedging({ ...fxHedging, end_spot_price: formatted });
                                                }
                                            }}
                                            onFocus={e => {
                                                const num = normalizeNumber(e.target.value);
                                                if (num !== null && !isNaN(num)) {
                                                    setFxHedging({ ...fxHedging, end_spot_price: num.toString() });
                                                }
                                            }}
                                            placeholder="0"
                                        />
                                    </span>
                                </div>
                                <div className="split-right">
                                    <span className="label">EUR/KRW Future Price</span>
                                    <span className="value">
                                        <span className="unit-left">₩</span>
                                        <input
                                            type="text"
                                            value={fxHedging.end_futures_price || ""}
                                            onChange={e => setFxHedging({ ...fxHedging, end_futures_price: e.target.value })}
                                            onBlur={e => {
                                                const formatted = formatNumber(e.target.value, "price");
                                                if (formatted !== e.target.value) {
                                                    setFxHedging({ ...fxHedging, end_futures_price: formatted });
                                                }
                                            }}
                                            onFocus={e => {
                                                const num = normalizeNumber(e.target.value);
                                                if (num !== null && !isNaN(num)) {
                                                    setFxHedging({ ...fxHedging, end_futures_price: num.toString() });
                                                }
                                            }}
                                            placeholder="0"
                                        />
                                    </span>
                                </div>
                            </div>

                            <div className="divider single"></div>

                            {/* Hedging - 병합 행 (타이틀만) */}
                            <div className="row merged-row title-row">
                                <span className="label">▶ Hedging</span>
                            </div>

                            {/* EU-CBAM Payment & Adj Hedge Ratio - 2열 */}
                            <div className="row split-row">
                                <div className="split-left">
                                    <span className="label">EU-CBAM Payment</span>
                                    <span className="value">
                                        <span className="unit-left">€</span>
                                        <input
                                            type="text"
                                            value={fxHedging.cbam_payment || ""}
                                            onChange={e => setFxHedging({ ...fxHedging, cbam_payment: e.target.value })}
                                            onBlur={e => {
                                                const formatted = formatNumber(e.target.value, "amount");
                                                if (formatted !== e.target.value) {
                                                    setFxHedging({ ...fxHedging, cbam_payment: formatted });
                                                }
                                            }}
                                            onFocus={e => {
                                                const num = normalizeNumber(e.target.value);
                                                if (num !== null && !isNaN(num)) {
                                                    setFxHedging({ ...fxHedging, cbam_payment: num.toString() });
                                                }
                                            }}
                                            placeholder="0"
                                        />
                                    </span>
                                </div>
                                <div className="split-right">
                                    <span className="label">Adj Hedge Ratio</span>
                                    <span className="value">
                                        <input
                                            type="text"
                                            value={fxHedging.adj_hedge_ratio || ""}
                                            onChange={e => setFxHedging({ ...fxHedging, adj_hedge_ratio: e.target.value })}
                                            onBlur={e => {
                                                const formatted = formatNumber(e.target.value, "ratio");
                                                if (formatted !== e.target.value) {
                                                    setFxHedging({ ...fxHedging, adj_hedge_ratio: formatted });
                                                }
                                            }}
                                            onFocus={e => {
                                                const num = normalizeNumber(e.target.value);
                                                if (num !== null && !isNaN(num)) {
                                                    setFxHedging({ ...fxHedging, adj_hedge_ratio: num.toString() });
                                                }
                                            }}
                                            placeholder="0"
                                        />
                                    </span>
                                </div>
                            </div>

                            <div className="divider double"></div>
                        </div>
                    </div>

                    <div className="button-row">
                        <button onClick={() => {
                            setFxHedging({});
                            setFxHedgingResult({});
                            setErrors({});
                        }}>Clear</button>
                        <button onClick={() => {
                            // 클라이언트 사이드 계산
                            const startDate = fxHedging.hedging_start_date;
                            const endDate = fxHedging.hedging_end_date;
                            const startSpot = normalizeNumber(fxHedging.start_spot_price);
                            const endSpot = normalizeNumber(fxHedging.end_spot_price);
                            const cbamPayment = normalizeNumber(fxHedging.cbam_payment);
                            const startFutures = normalizeNumber(fxHedging.start_futures_price);
                            const endFutures = normalizeNumber(fxHedging.end_futures_price);
                            const adjHedgeRatio = normalizeNumber(fxHedging.adj_hedge_ratio);

                            // FX EUR/KRW Spot P/L = [(6) - (2)] * (4) * (-1)
                            const spotPL = (endSpot - startSpot) * cbamPayment * (-1);

                            // FX EUR/KRW Futures P/L = [(6) - (2)] / 0.1 * 10000 * 0.1 * [(3) / 10000 * (8)]
                            const futuresPL = ((endFutures - startFutures) / 0.1) * 10000 * 0.1 * ((cbamPayment / 10000) * adjHedgeRatio);

                            // Net EUR/KRW Hedging P/L
                            const netPL = spotPL + futuresPL;

                            setFxHedgingResult({
                                start_date: startDate,
                                end_date: endDate,
                                start_spot: startSpot,
                                end_spot: endSpot,
                                start_futures: startFutures,
                                end_futures: endFutures,
                                spot_pl: spotPL,
                                futures_pl: futuresPL,
                                net_pl: netPL
                            });
                        }}>Calculate</button>
                    </div>

                    <FxHedgingResults result={fxHedgingResult} />
                </>
            )}

            {/* =================================================
                EUA HEDGING
                ================================================= */}
            {version === "eua-hedging" && (
                <>
                    <div className="fx-hedging-wrapper">
                        <div className="fx-hedging-headers">
                            <div className="fx-section-header">EU-CBAM Certifications Hedging Strategy</div>
                            <div className="fx-section-header fx-green">EU-ETS EUA Futures Long Hedging Position</div>
                        </div>

                        <div className="card">
                            {/* Start Date - 병합 행 */}
                            <div className="row merged-row">
                                <span className="label">▶ Start Date</span>
                                <span className="value">
                                    <input
                                        type="date"
                                        value={euaHedging.hedging_start_date || ""}
                                        onChange={e => setEuaHedging({ ...euaHedging, hedging_start_date: e.target.value })}
                                    />
                                </span>
                            </div>

                            {/* Spot Price & Future Price - 2열 */}
                            <div className="row split-row">
                                <div className="split-left">
                                    <span className="label">EU-ETS EUA Spot Price</span>
                                    <span className="value">
                                        <span className="unit-left">€</span>
                                        <input
                                            type="text"
                                            value={euaHedging.start_spot_price || ""}
                                            onChange={e => setEuaHedging({ ...euaHedging, start_spot_price: e.target.value })}
                                            onBlur={e => {
                                                const formatted = formatNumber(e.target.value, "price");
                                                if (formatted !== e.target.value) {
                                                    setEuaHedging({ ...euaHedging, start_spot_price: formatted });
                                                }
                                            }}
                                            onFocus={e => {
                                                const num = normalizeNumber(e.target.value);
                                                if (num !== null && !isNaN(num)) {
                                                    setEuaHedging({ ...euaHedging, start_spot_price: num.toString() });
                                                }
                                            }}
                                            placeholder="0"
                                        />
                                    </span>
                                </div>
                                <div className="split-right">
                                    <span className="label">EU-ETS EUA Futures Price</span>
                                    <span className="value">
                                        <span className="unit-left">€</span>
                                        <input
                                            type="text"
                                            value={euaHedging.start_futures_price || ""}
                                            onChange={e => setEuaHedging({ ...euaHedging, start_futures_price: e.target.value })}
                                            onBlur={e => {
                                                const formatted = formatNumber(e.target.value, "price");
                                                if (formatted !== e.target.value) {
                                                    setEuaHedging({ ...euaHedging, start_futures_price: formatted });
                                                }
                                            }}
                                            onFocus={e => {
                                                const num = normalizeNumber(e.target.value);
                                                if (num !== null && !isNaN(num)) {
                                                    setEuaHedging({ ...euaHedging, start_futures_price: num.toString() });
                                                }
                                            }}
                                            placeholder="0"
                                        />
                                    </span>
                                </div>
                            </div>

                            {/* End Date - 병합 행 */}
                            <div className="row merged-row">
                                <span className="label">▶ End Date</span>
                                <span className="value">
                                    <input
                                        type="date"
                                        value={euaHedging.hedging_end_date || ""}
                                        onChange={e => setEuaHedging({ ...euaHedging, hedging_end_date: e.target.value })}
                                    />
                                </span>
                            </div>

                            {/* Spot Price & Future Price - 2열 */}
                            <div className="row split-row">
                                <div className="split-left">
                                    <span className="label">EU-ETS EUA Spot Price</span>
                                    <span className="value">
                                        <span className="unit-left">€</span>
                                        <input
                                            type="text"
                                            value={euaHedging.end_spot_price || ""}
                                            onChange={e => setEuaHedging({ ...euaHedging, end_spot_price: e.target.value })}
                                            onBlur={e => {
                                                const formatted = formatNumber(e.target.value, "price");
                                                if (formatted !== e.target.value) {
                                                    setEuaHedging({ ...euaHedging, end_spot_price: formatted });
                                                }
                                            }}
                                            onFocus={e => {
                                                const num = normalizeNumber(e.target.value);
                                                if (num !== null && !isNaN(num)) {
                                                    setEuaHedging({ ...euaHedging, end_spot_price: num.toString() });
                                                }
                                            }}
                                            placeholder="0"
                                        />
                                    </span>
                                </div>
                                <div className="split-right">
                                    <span className="label">EU-ETS EUA Futures Price</span>
                                    <span className="value">
                                        <span className="unit-left">€</span>
                                        <input
                                            type="text"
                                            value={euaHedging.end_futures_price || ""}
                                            onChange={e => setEuaHedging({ ...euaHedging, end_futures_price: e.target.value })}
                                            onBlur={e => {
                                                const formatted = formatNumber(e.target.value, "price");
                                                if (formatted !== e.target.value) {
                                                    setEuaHedging({ ...euaHedging, end_futures_price: formatted });
                                                }
                                            }}
                                            onFocus={e => {
                                                const num = normalizeNumber(e.target.value);
                                                if (num !== null && !isNaN(num)) {
                                                    setEuaHedging({ ...euaHedging, end_futures_price: num.toString() });
                                                }
                                            }}
                                            placeholder="0"
                                        />
                                    </span>
                                </div>
                            </div>

                            <div className="divider single"></div>

                            {/* Hedging - 병합 행 (타이틀만) */}
                            <div className="row merged-row title-row">
                                <span className="label">▶ Hedging</span>
                            </div>

                            {/* Number of Certifications & Adj Hedge Ratio - 2열 */}
                            <div className="row split-row">
                                <div className="split-left">
                                    <span className="label">Number of Certifications</span>
                                    <span className="value">
                                        <input
                                            type="text"
                                            value={euaHedging.num_certifications || ""}
                                            onChange={e => setEuaHedging({ ...euaHedging, num_certifications: e.target.value })}
                                            onBlur={e => {
                                                const formatted = formatNumber(e.target.value, "amount");
                                                if (formatted !== e.target.value) {
                                                    setEuaHedging({ ...euaHedging, num_certifications: formatted });
                                                }
                                            }}
                                            onFocus={e => {
                                                const num = normalizeNumber(e.target.value);
                                                if (num !== null && !isNaN(num)) {
                                                    setEuaHedging({ ...euaHedging, num_certifications: num.toString() });
                                                }
                                            }}
                                            placeholder="0"
                                        />
                                    </span>
                                </div>
                                <div className="split-right">
                                    <span className="label">Adj Hedge Ratio</span>
                                    <span className="value">
                                        <input
                                            type="text"
                                            value={euaHedging.adj_hedge_ratio || ""}
                                            onChange={e => setEuaHedging({ ...euaHedging, adj_hedge_ratio: e.target.value })}
                                            onBlur={e => {
                                                const formatted = formatNumber(e.target.value, "ratio");
                                                if (formatted !== e.target.value) {
                                                    setEuaHedging({ ...euaHedging, adj_hedge_ratio: formatted });
                                                }
                                            }}
                                            onFocus={e => {
                                                const num = normalizeNumber(e.target.value);
                                                if (num !== null && !isNaN(num)) {
                                                    setEuaHedging({ ...euaHedging, adj_hedge_ratio: num.toString() });
                                                }
                                            }}
                                            placeholder="0"
                                        />
                                    </span>
                                </div>
                            </div>

                            <div className="divider double"></div>
                        </div>
                    </div>

                    <div className="button-row">
                        <button onClick={() => {
                            setEuaHedging({});
                            setEuaHedgingResult({});
                            setErrors({});
                        }}>Clear</button>
                        <button onClick={() => {
                            // 클라이언트 사이드 계산
                            const startDate = euaHedging.hedging_start_date;
                            const endDate = euaHedging.hedging_end_date;
                            const startSpot = normalizeNumber(euaHedging.start_spot_price);
                            const startFutures = normalizeNumber(euaHedging.start_futures_price);
                            const numCertifications = normalizeNumber(euaHedging.num_certifications);
                            const endSpot = normalizeNumber(euaHedging.end_spot_price);
                            const endFutures = normalizeNumber(euaHedging.end_futures_price);
                            const adjHedgeRatio = normalizeNumber(euaHedging.adj_hedge_ratio);

                            // EU-ETS EUA Spot P/L = (endSpot - startSpot) * numCertifications * (-1)
                            const spotPL = (endSpot - startSpot) * numCertifications * (-1);

                            // EU-ETS EUA Futures P/L = ((endFutures - startFutures) / 0.01) * 10 * ((numCertifications / 1000) * adjHedgeRatio)
                            const futuresPL = ((endFutures - startFutures) / 0.01) * 10 * ((numCertifications / 1000) * adjHedgeRatio);

                            // Net EUA Hedging P/L
                            const netPL = spotPL + futuresPL;

                            setEuaHedgingResult({
                                start_date: startDate,
                                end_date: endDate,
                                start_spot: startSpot,
                                end_spot: endSpot,
                                start_futures: startFutures,
                                end_futures: endFutures,
                                spot_pl: spotPL,
                                futures_pl: futuresPL,
                                net_pl: netPL
                            });
                        }}>Calculate</button>
                    </div>

                    <EuaHedgingResults result={euaHedgingResult} />
                </>
            )}

            <div className="footer">
                | Copyright © 2026 NAMU EnR Co., Ltd., All rights reserved. |
            </div>
        </div>
    );
}

/* ================= Components ================= */

const InputCard = ({ fields, state, setState, errors }) => {
    const handleInputChange = (key, value) => {
        setState({ ...state, [key]: value });
    };

    const handleInputBlur = (key, value, unit) => {
        // date 타입은 포맷팅하지 않음
        if (unit === "date") return;

        const fieldType = getFieldType(key);
        const formatted = formatNumber(value, fieldType);
        if (formatted !== value) {
            setState({ ...state, [key]: formatted });
        }
    };

    const handleInputFocus = (key, value, unit) => {
        // date 타입은 포맷팅 제거하지 않음
        if (unit === "date") return;

        // 포커스 시 포맷팅 제거하여 편집하기 쉽게
        const num = normalizeNumber(value);
        if (num !== null && !isNaN(num)) {
            setState({ ...state, [key]: num.toString() });
        }
    };

    return (
        <div className="card">
            {fields.map((field, index) => {
                // 구분선 처리
                if (typeof field === 'object' && field.type === 'divider') {
                    return (
                        <div key={`divider-${index}`} className={`divider ${field.style}`}></div>
                    );
                }

                // 타이틀 처리 (입력란 없이 텍스트만)
                if (typeof field === 'object' && field.type === 'title') {
                    return (
                        <div key={`title-${index}`} className="row title-row">
                            <span className="label">{field.text}</span>
                        </div>
                    );
                }

                // 빈 공간 처리 (높이 맞추기용)
                if (typeof field === 'object' && field.type === 'spacer') {
                    return <div key={`spacer-${index}`} className="row spacer-row"></div>;
                }

                // 일반 입력 필드 처리
                const [label, key, unit] = field;
                const isDateField = unit === "date";

                // unit이 "spacer"인 경우 빈 행 렌더링
                if (unit === "spacer") {
                    return <div key={`spacer-${index}`} className="row spacer-row"></div>;
                }

                // 빈 라벨과 키인 경우 빈 행 렌더링
                if (!label && !key) {
                    return <div key={`empty-${index}`} className="row empty-row"></div>;
                }

                return (
                    <div className="row" key={key || `row-${index}`}>
                        <span className="label">{label}</span>
                        <span className="value">
                            <span className="unit-left">{(unit === "€" || unit === "₩") ? unit : ""}</span>
                            <input
                                type={isDateField ? "date" : "text"}
                                value={state[key] || ""}
                                onChange={e => handleInputChange(key, e.target.value)}
                                onBlur={e => handleInputBlur(key, e.target.value, unit)}
                                onFocus={e => handleInputFocus(key, e.target.value, unit)}
                                className={errors[key] ? "input-error shake" : ""}
                                placeholder={isDateField ? "" : "0"}
                            />
                            <span className="unit-right">{unit === "%" ? "%" : ""}</span>
                        </span>
                        {errors[key] && (
                            <div className="inline-error">{errors[key]}</div>
                        )}
                    </div>
                );
            })}
            <div className="divider double"></div>
        </div>
    );
};


